import { z } from 'zod';
import { request } from '@/lib/api';
import { intlLocale } from '@/lib/i18n';
import { userRoleSchema } from '@/lib/schemas';

/* ---------------------------------------------------------------------------
   סידור עבודה (GET /schedule, PATCH /schedule/tasks/:id, שעות וחופשים).
   כל התאריכים בלוח הם תאריכים מקומיים בישראל — ביקור ב-23:30 שייך ליום שלו
   גם אם ב-UTC הוא כבר מחר.
   --------------------------------------------------------------------------- */

const daySchema = z.object({ start: z.string(), end: z.string() }).nullable().optional();
export const workHoursSchema = z.object({
  0: daySchema,
  1: daySchema,
  2: daySchema,
  3: daySchema,
  4: daySchema,
  5: daySchema,
  6: daySchema,
});
export type WorkHours = z.infer<typeof workHoursSchema>;
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const customer = z.object({ name: z.string(), address: z.string().nullish() });
const taskStatus = z.enum(['NEW', 'ASSIGNED', 'IN_PROGRESS', 'CLOSED', 'CANCELLED']);

export const scheduledTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: taskStatus,
  scheduledStart: z.string().nullable(),
  scheduledEnd: z.string().nullable(),
  assignedToUserId: z.string().nullable(),
  customer,
});
export type ScheduledTask = z.infer<typeof scheduledTaskSchema>;

export const boardSchema = z.object({
  members: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      role: userRoleSchema,
      skills: z.array(z.string()),
      workHours: workHoursSchema.nullable(),
    }),
  ),
  tasks: z.array(scheduledTaskSchema),
  unscheduled: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: taskStatus,
      assignedToUserId: z.string().nullable(),
      createdAt: z.string(),
      customer,
    }),
  ),
  timeOff: z.array(
    z.object({ id: z.string(), userId: z.string(), startDate: z.string(), endDate: z.string(), reason: z.string().nullable() }),
  ),
});
export type Board = z.infer<typeof boardSchema>;
export type Member = Board['members'][number];

const warningSchema = z.union([
  z.object({ type: z.literal('conflict'), taskId: z.string(), title: z.string() }),
  z.object({ type: z.enum(['time_off', 'day_off', 'outside_hours']), detail: z.string() }),
]);
export type Warning = z.infer<typeof warningSchema>;

export const SCHEDULE_KEY = ['schedule'];

export const slotSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  start: z.string(),
  end: z.string(),
  distanceKm: z.number().nullable(),
  from: z.enum(['previous_job', 'home']).nullable(),
  jobsThatDay: z.number(),
  score: z.number(),
  reasons: z.array(z.string()),
});
export type Slot = z.infer<typeof slotSchema>;

const suggestionSchema = z.object({
  slots: z.array(slotSchema),
  skillFallback: z.boolean(),
  durationMinutes: z.number(),
  readyFrom: z.string(),
  customerLocated: z.boolean(),
});
export type Suggestions = z.infer<typeof suggestionSchema>;

export const scheduleApi = {
  board: (from: string, signal?: AbortSignal) =>
    request(`/schedule?from=${from}&days=7`, { schema: boardSchema, signal }),
  move: (taskId: string, body: { assignedToUserId?: string | null; scheduledStart?: string | null; scheduledEnd?: string | null }) =>
    request(`/schedule/tasks/${taskId}`, {
      method: 'PATCH',
      body,
      schema: z.object({ task: scheduledTaskSchema, warnings: z.array(warningSchema) }),
    }),
  setWorkHours: (userId: string, workHours: WorkHours | null) =>
    request(`/schedule/members/${userId}/work-hours`, { method: 'PUT', body: { workHours } }),
  addTimeOff: (body: { userId: string; startDate: string; endDate: string; reason?: string }) =>
    request('/schedule/time-off', { method: 'POST', body }),
  removeTimeOff: (id: string) => request(`/schedule/time-off/${id}`, { method: 'DELETE' }),
  suggest: (body: { customerId: string; jobTypeTemplateId?: string; durationMinutes?: number; readyFrom?: string }) =>
    request('/schedule/suggest', { method: 'POST', body, schema: suggestionSchema }),
};

// --- תאריכים בשעון ישראל -----------------------------------------------------

const PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Jerusalem',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** תאריך ושעה מקומיים ("2026-10-04", "09:30"). */
export function local(d: Date | string): { date: string; time: string } {
  const p = Object.fromEntries(PARTS.formatToParts(new Date(d)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** תאריך + שעה בישראל → Date (UTC). מתקן את ההפרש, כולל מעבר שעון קיץ. */
export function fromLocal(date: string, time: string): Date {
  const guess = new Date(`${date}T${time}:00Z`);
  const shown = local(guess);
  const diffMs = new Date(`${shown.date}T${shown.time}:00Z`).getTime() - guess.getTime();
  return new Date(guess.getTime() - diffMs);
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** יום ראשון של השבוע שבו נמצא התאריך. */
export function weekStart(date: string): string {
  const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
  return addDays(date, -dow);
}

export const weekdayOf = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay() as Weekday;

/** שם היום בשפת הממשק (0 = ראשון). 2023-01-01 היה יום ראשון. */
const weekdayFmt = new Intl.DateTimeFormat(intlLocale(), { weekday: 'long', timeZone: 'UTC' });
export const WEEKDAYS: string[] = Array.from({ length: 7 }, (_, d) => weekdayFmt.format(new Date(Date.UTC(2023, 0, 1 + d, 12))));
